import * as React from 'react';
import { styled } from '@linaria/react';

import {
  normalize,
  roundTo,
  convertDegreesToRadians,
  convertPolarToCartesian,
} from '@/utils';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';
import SvgWithGrid from '@/components/SvgWithGrid';

import { CodeSnippet } from '../friendly-introduction-to-svgs/BasicShapesDemo/BasicShapes.shared';

const SIZE = 16;
const INSET_BY = 3;

function getPolylinePoints(angleDeg: number) {
  const x1 = INSET_BY;
  const y1 = SIZE - INSET_BY;
  const x2 = SIZE - INSET_BY;
  const y2 = SIZE - INSET_BY;

  const actualAngle = normalize(angleDeg, 0, 90, 180, 270);
  const angleRad = convertDegreesToRadians(actualAngle);
  const distance = x2 - x1;
  const rawPolar = convertPolarToCartesian([angleRad, distance]);

  return [
    [x1, y1],
    [x2, y2],
    [x2 + rawPolar[0], y2 + rawPolar[1]],
  ];
}

function MiterAngleDemo() {
  return (
    <Demo
      id="miter-angle-demo"
      outputWrapper={OutputWrapper}
      initialValues={{
        angle: 45,
      }}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          key="angle"
          id="angle"
          label="angle"
          min={5}
          max={60}
          step={1}
        />,
      ]}
    >
      {({ angle }) => {
        const points = getPolylinePoints(angle);
        const pointsStringActual = `M ${points[0][0]},${points[0][1]}
 L ${points[1][0]},${points[1][1]} L ${points[2][0]},${points[2][1]}`;

        const pointsStringPresentational = `\
      M ${points[0][0]},${points[0][1]}
      L ${points[1][0]},${points[1][1]}
      L ${roundTo(points[2][0], 1)},${roundTo(points[2][1], 1)}`;

        const code = `\
<svg fill="none">
  <path
    d="
${pointsStringPresentational}
    "
  />
</svg>\
`;
        return (
          <Wrapper>
            <StyledCodeSnippet code={code} />

            <SvgWrapper>
              <Svg
                gridGap={1}
                lineWidth={0.1}
                viewBox={`0 0 ${SIZE} ${SIZE}`}
              >
                <path
                  d={pointsStringActual}
                  strokeWidth={1}
                  stroke="var(--wham-primary-500)"
                  fill="none"
                  strokeLinejoin="miter"
                />
                <path
                  d={pointsStringActual}
                  fill="none"
                  strokeWidth={0.2}
                  strokeDasharray="0.6 0.5"
                  strokeLinejoin="miter"
                  strokeLinecap="round"
                  stroke="hsl(210deg 15% 6% / 0.5)"
                />
                <path
                  d={pointsStringActual}
                  fill="none"
                  strokeWidth={0.1}
                  strokeLinejoin="miter"
                  strokeDasharray="0.6 0.5"
                  stroke="white"
                />
              </Svg>
            </SvgWrapper>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

const HIDE_SNIPPET_AT = '33rem';

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: transparent;
`;

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: min(400px, 100%);
  justify-content: center;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;
  gap: 16px;
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  --inline-padding-override: 1.5rem;
  --block-padding-override: 1rem;
  background: var(--color-background);

  @container (max-width: ${HIDE_SNIPPET_AT}) {
    display: none;
  }
`;

const SvgWrapper = styled.div`
  display: flex;
  background: var(--color-background);

  @container (max-width: ${HIDE_SNIPPET_AT}) {
    position: relative;
  }
`;

const Svg = styled(SvgWithGrid)`
  display: block;
  width: 100%;
  max-width: 400px;
  aspect-ratio: 1;
  border-radius: 4px;
  outline: 1px solid var(--color-text);
`;

export default MiterAngleDemo;
